import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { 
  Play, Film, Radio, ArrowRight, Tv, Music, Ticket, 
  Crown, User, LogOut, Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ChooseDestination = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      navigate('/live/login?redirect=/choose-destination');
      return;
    }
    
    const customerData = localStorage.getItem('livestreamCustomer');
    if (customerData) {
      setCustomer(JSON.parse(customerData));
    }
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    navigate('/');
  };

  if (!customer) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 bg-black/50 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <Home className="h-5 w-5" />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          
          <img 
            src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png" 
            alt="LTV Production" 
            className="h-8 w-auto"
          />
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-purple-500 flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <span className="hidden sm:inline text-sm text-gray-300">
                {customer.full_name || customer.email?.split('@')[0]}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="text-gray-400 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="max-w-4xl w-full text-center">
          {/* Welcome Message */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Welcome, <span className="bg-gradient-to-r from-red-400 to-purple-400 bg-clip-text text-transparent">
              {customer.full_name || customer.email?.split('@')[0]}
            </span>
          </h1>
          <p className="text-gray-400 text-lg mb-12">
            Where would you like to go?
          </p>

          {/* Destination Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* LTV Live Card */}
            <button
              onClick={() => navigate('/live')}
              className="group relative p-8 rounded-3xl bg-gradient-to-br from-red-900/30 to-red-950/50 border border-red-500/30 hover:border-red-500/60 transition-all duration-300 text-left overflow-hidden"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Radio className="h-8 w-8 text-red-400" />
                </div>
                
                <h2 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  LTV Live
                  <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h2>
                
                <p className="text-gray-400 mb-6">
                  Watch live events, concerts, and exclusive broadcasts in real-time
                </p>
                
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-300 text-sm flex items-center gap-1">
                    <Radio className="h-3 w-3" /> Live Streaming
                  </span>
                  <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-300 text-sm flex items-center gap-1">
                    <Ticket className="h-3 w-3" /> Event Tickets
                  </span>
                </div>
              </div>
            </button>

            {/* VOD Card */}
            <button
              onClick={() => navigate('/vod')}
              className="group relative p-8 rounded-3xl bg-gradient-to-br from-purple-900/30 to-purple-950/50 border border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 text-left overflow-hidden"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Film className="h-8 w-8 text-purple-400" />
                </div>
                
                <h2 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  Video On Demand
                  <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h2>
                
                <p className="text-gray-400 mb-6">
                  Browse our library of shows, podcasts, concerts, and exclusive content
                </p>
                
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-sm flex items-center gap-1">
                    <Tv className="h-3 w-3" /> Shows & Podcasts
                  </span>
                  <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-sm flex items-center gap-1">
                    <Crown className="h-3 w-3" /> Membership
                  </span>
                </div>
              </div>
            </button>
          </div>

          {/* Quick Links */}
          <div className="mt-12 flex flex-wrap justify-center gap-4 text-sm">
            <button
              onClick={() => navigate('/live/account')}
              className="text-gray-500 hover:text-white transition-colors flex items-center gap-1"
            >
              <User className="h-4 w-4" /> My Account
            </button>
            <span className="text-gray-700">|</span>
            <button
              onClick={() => navigate('/live/my-tickets')}
              className="text-gray-500 hover:text-white transition-colors flex items-center gap-1"
            >
              <Ticket className="h-4 w-4" /> My Tickets
            </button>
            <span className="text-gray-700">|</span>
            <button
              onClick={() => navigate('/vod/my-library')}
              className="text-gray-500 hover:text-white transition-colors flex items-center gap-1"
            >
              <Film className="h-4 w-4" /> My Library
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChooseDestination;
